"use strict";
/**
 * Sparse Overlay Solver
 *
 * This module implements the sparse overlay representation for efficient
 * storage of segment membership and flag values.
 *
 * Key principles:
 * - Automatic polarity optimization (choose default + exceptions that minimizes writes)
 * - Version-based staleness detection
 * - Support for inverted segments ("everyone but X")
 *
 * The core formula:
 *   value(uid) = defaultValue XOR exceptionPresent(uid)
 *
 * This allows representing both:
 * - "Only users in X get true" → default=false, exceptions=X
 * - "Everyone except X gets true" → default=true, exceptions=X
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.VersionMetadataSchema = exports.PublishingStateSchema = exports.RuntimePointerSchema = exports.UserMembershipDocSchema = exports.OverlayExceptionSchema = exports.BooleanOverlaySchema = exports.OverlayPolarity = void 0;
exports.optimizePolarity = optimizePolarity;
exports.evaluateOverlay = evaluateOverlay;
exports.createBooleanOverlay = createBooleanOverlay;
exports.createOverlayException = createOverlayException;
exports.createUserMembershipDoc = createUserMembershipDoc;
exports.isMembershipDocStale = isMembershipDocStale;
exports.computeMembershipDiff = computeMembershipDiff;
exports.createRuntimePointer = createRuntimePointer;
exports.createVersionMetadata = createVersionMetadata;
const zod_1 = require("zod");
/**
 * Polarity of a sparse overlay
 */
exports.OverlayPolarity = zod_1.z.enum([
    "positive", // default=false, exceptions are members (most users get false)
    "negative", // default=true, exceptions are non-members (most users get true)
]);
/**
 * Sparse overlay for boolean values
 *
 * Stores only the exceptions to the default value.
 */
exports.BooleanOverlaySchema = zod_1.z.object({
    /**
     * The default value for users not in the exceptions set
     */
    defaultValue: zod_1.z.boolean(),
    /**
     * Polarity of this overlay (for documentation/debugging)
     */
    polarity: exports.OverlayPolarity,
    /**
     * Version of this overlay (monotonically increasing)
     * Used for staleness detection
     */
    version: zod_1.z.number(),
    /**
     * When this overlay was last computed
     */
    computedAt: zod_1.z.number(),
    /**
     * Number of exceptions (for metrics)
     */
    exceptionCount: zod_1.z.number(),
});
/**
 * Exception entry for a user
 */
exports.OverlayExceptionSchema = zod_1.z.object({
    /**
     * User ID
     */
    uid: zod_1.z.string(),
    /**
     * The value for this user (opposite of default)
     */
    value: zod_1.z.boolean(),
    /**
     * Version this exception was computed for
     */
    version: zod_1.z.number(),
    /**
     * When this exception was written
     */
    writtenAt: zod_1.z.number(),
});
/**
 * Per-user consolidated membership document
 *
 * Instead of storing membership per-segment (which requires N reads),
 * we consolidate all segment memberships for a user into one document.
 */
exports.UserMembershipDocSchema = zod_1.z.object({
    /**
     * User ID
     */
    uid: zod_1.z.string(),
    /**
     * Version of the segments this was computed for
     */
    segmentsVersion: zod_1.z.number(),
    /**
     * Segment IDs the user is a member of
     * (Only stores IDs, not names, for privacy)
     */
    segments: zod_1.z.array(zod_1.z.string()),
    /**
     * Server-computed derived attributes
     * (e.g., isInternal, cohort)
     */
    derivedAttributes: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).default({}),
    /**
     * When this was last computed
     */
    computedAt: zod_1.z.number(),
});
/**
 * Optimize polarity for a boolean predicate
 *
 * Given a set of users who should get "true" and the total user population,
 * determine whether to use positive polarity (default=false, exceptions=members)
 * or negative polarity (default=true, exceptions=non-members).
 *
 * @param membersWhoGetTrue - Set of user IDs who should get true
 * @param totalPopulation - Total number of users (optional, for estimation)
 * @param allUserIds - Complete set of all user IDs (required if negative polarity might be optimal)
 * @returns Optimization result with recommended polarity
 */
function optimizePolarity(membersWhoGetTrue, totalPopulation, allUserIds) {
    const memberCount = membersWhoGetTrue.size;
    // If we know the total population, we can make a precise decision
    if (totalPopulation !== undefined) {
        const nonMemberCount = totalPopulation - memberCount;
        if (memberCount <= nonMemberCount) {
            // Positive polarity: default=false, exceptions=members
            return {
                polarity: "positive",
                defaultValue: false,
                exceptions: membersWhoGetTrue,
                writesSaved: nonMemberCount - memberCount,
            };
        }
        else {
            // Negative polarity: default=true, exceptions=non-members
            // We need the complete user set to compute non-members
            if (!allUserIds) {
                throw new Error("Cannot use negative polarity optimization: allUserIds is required to compute non-members. " +
                    `Members: ${memberCount}, Non-members: ${nonMemberCount}. ` +
                    "Either provide allUserIds or use positive polarity.");
            }
            // Compute non-members as allUserIds - membersWhoGetTrue
            const nonMembers = new Set();
            for (const uid of allUserIds) {
                if (!membersWhoGetTrue.has(uid)) {
                    nonMembers.add(uid);
                }
            }
            return {
                polarity: "negative",
                defaultValue: true,
                exceptions: nonMembers,
                writesSaved: memberCount - nonMemberCount,
            };
        }
    }
    // Without total population, default to positive polarity
    // This is the safer choice as it only writes for actual members
    return {
        polarity: "positive",
        defaultValue: false,
        exceptions: membersWhoGetTrue,
        writesSaved: 0,
    };
}
/**
 * Determine if a user is a member based on overlay and exception
 *
 * @param overlay - The overlay configuration
 * @param hasException - Whether the user has an exception entry
 * @param exceptionVersion - Version of the exception (if present)
 * @returns Whether the user is a member
 */
function evaluateOverlay(overlay, hasException, exceptionVersion) {
    // If no exception, return default
    if (!hasException) {
        return overlay.defaultValue;
    }
    // If exception version doesn't match current version, it's stale
    // Treat stale exceptions as non-existent (return default)
    if (exceptionVersion !== undefined && exceptionVersion !== overlay.version) {
        return overlay.defaultValue;
    }
    // Exception present and valid: return opposite of default
    return !overlay.defaultValue;
}
/**
 * Create a new boolean overlay
 */
function createBooleanOverlay(polarity, exceptionCount, version) {
    return {
        defaultValue: polarity === "negative",
        polarity,
        version,
        computedAt: Date.now(),
        exceptionCount,
    };
}
/**
 * Create an exception entry
 */
function createOverlayException(uid, value, version) {
    return {
        uid,
        value,
        version,
        writtenAt: Date.now(),
    };
}
/**
 * Create a user membership document
 */
function createUserMembershipDoc(uid, segmentsVersion, segments, derivedAttributes = {}) {
    return {
        uid,
        segmentsVersion,
        segments,
        derivedAttributes,
        computedAt: Date.now(),
    };
}
/**
 * Check if a user membership doc is stale
 */
function isMembershipDocStale(doc, currentSegmentsVersion) {
    return doc.segmentsVersion !== currentSegmentsVersion;
}
/**
 * Compute the minimal write set for a segment membership change
 *
 * Given old and new member sets, compute which users need writes.
 *
 * @param oldMembers - Previous member set
 * @param newMembers - New member set
 * @param overlay - Current overlay configuration
 * @returns Users who need exception writes and users whose exceptions should be removed
 */
function computeMembershipDiff(oldMembers, newMembers, overlay) {
    const addExceptions = new Set();
    const removeExceptions = new Set();
    if (overlay.polarity === "positive") {
        // Positive polarity: exceptions are members
        // Add exceptions for new members not in old set
        for (const uid of newMembers) {
            if (!oldMembers.has(uid)) {
                addExceptions.add(uid);
            }
        }
        // Remove exceptions for old members not in new set
        for (const uid of oldMembers) {
            if (!newMembers.has(uid)) {
                removeExceptions.add(uid);
            }
        }
    }
    else {
        // Negative polarity: exceptions are non-members
        // Add exceptions for users who left (were members, now aren't)
        for (const uid of oldMembers) {
            if (!newMembers.has(uid)) {
                addExceptions.add(uid);
            }
        }
        // Remove exceptions for users who joined (weren't members, now are)
        for (const uid of newMembers) {
            if (!oldMembers.has(uid)) {
                removeExceptions.add(uid);
            }
        }
    }
    return { addExceptions, removeExceptions };
}
/**
 * Runtime pointer for multi-phase publishing
 *
 * This document controls which version of the flag system is active.
 * The pointer is only updated after all writes for a new version are complete.
 */
exports.RuntimePointerSchema = zod_1.z.object({
    /**
     * Environment this pointer is for
     */
    environment: zod_1.z.string(),
    /**
     * Currently active version (what everyone reads)
     */
    activeVersion: zod_1.z.string(),
    /**
     * Version being built (not yet active)
     */
    buildingVersion: zod_1.z.string().optional(),
    /**
     * Version for shadow/canary testing
     */
    shadowVersion: zod_1.z.string().optional(),
    /**
     * Segment that sees the shadow version
     */
    shadowAudience: zod_1.z.string().optional(),
    /**
     * When the active version was published
     */
    publishedAt: zod_1.z.number(),
    /**
     * Who published the active version
     */
    publishedBy: zod_1.z.string().optional(),
});
/**
 * Publishing state for a version
 */
exports.PublishingStateSchema = zod_1.z.enum([
    "draft", // Being edited
    "building", // Materializing overlays
    "shadow", // Canary testing
    "active", // Live for all users
    "archived", // No longer active
]);
/**
 * Version metadata
 */
exports.VersionMetadataSchema = zod_1.z.object({
    /**
     * Version identifier
     */
    version: zod_1.z.string(),
    /**
     * Current state
     */
    state: exports.PublishingStateSchema,
    /**
     * When this version was created
     */
    createdAt: zod_1.z.number(),
    /**
     * Who created this version
     */
    createdBy: zod_1.z.string().optional(),
    /**
     * When building started (if applicable)
     */
    buildStartedAt: zod_1.z.number().optional(),
    /**
     * When building completed (if applicable)
     */
    buildCompletedAt: zod_1.z.number().optional(),
    /**
     * When shadow testing started (if applicable)
     */
    shadowStartedAt: zod_1.z.number().optional(),
    /**
     * When this version went active (if applicable)
     */
    activatedAt: zod_1.z.number().optional(),
    /**
     * Hash of the compiled bundle for integrity checking
     */
    bundleHash: zod_1.z.string().optional(),
});
/**
 * Create a new runtime pointer
 */
function createRuntimePointer(environment, activeVersion) {
    return {
        environment,
        activeVersion,
        publishedAt: Date.now(),
    };
}
/**
 * Create version metadata
 */
function createVersionMetadata(version, createdBy) {
    return {
        version,
        state: "draft",
        createdAt: Date.now(),
        createdBy,
    };
}
//# sourceMappingURL=sparse-overlay.js.map